// Cross-component navigation primitive.
//
// Posted has no router — currentPage / inventoryTab / menuTab / operationsTab
// are all React state in App.js. Cross-component navigation (e.g. the Inventory
// detail drawer's "View in Price Tracker →" footer) used to fire a CustomEvent
// named `operations:navigate` that Operations.js listened for. With the
// operations/inventory/menu group split, navigation can cross top-level
// pages, so the event was generalized.
//
// USAGE
//   import { navigate } from './lib/navigate';
//   navigate('inventory', 'pricetracker');                    // top-level page + sub-tab
//   navigate('inventory', 'invoices', { period, anchor });    // pre-fill payload (Invoices)
//   navigate('operations', 'food');                           // cross-page link
//
// App.js owns the single listener and routes the payload to the right state
// setters. Pages that read `config` (Invoices' "Open invoices →" pre-fill) do
// so via a one-shot useEffect and clear the config after consumption so
// re-navigating doesn't re-trigger.

export const NAV_EVENT = 'app:navigate';

export function navigate(page, tab, config) {
  if (typeof window === 'undefined') return;
  const detail = { page };
  if (tab != null) detail.tab = tab;
  if (config != null) detail.config = config;
  window.dispatchEvent(new CustomEvent(NAV_EVENT, { detail }));
}

// ─── Invoice Review modal opener ─────────────────────────────────────────
// App.js renders <InvoiceReviewModal> globally and listens for this event.
// Any surface that wants to open the modal (live toast CTA, Invoices row
// "Review" button, anywhere we link to a parsed invoice) calls
// `openInvoiceReview(invoiceId)` instead of mounting its own modal.
//
// The reason this is a global event rather than prop-drilled state: the
// modal can be triggered from a transient toast that's mounted at the App
// root, and from page components several levels deep — neither has clean
// access to the other. CustomEvent keeps the modal owner (App.js) as the
// single source of truth.
export const OPEN_INVOICE_REVIEW_EVENT = 'app:open-invoice-review';

export function openInvoiceReview(invoiceId) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_INVOICE_REVIEW_EVENT, { detail: { invoiceId } }));
}
